/**
 * İSİM ÇAKIŞMASI SÖZLÜĞÜ — ölçütün beslendiği TABLOLAR.
 *
 * `organization-name-similarity.ts`ten AYRILDI: ölçüt (katlama, mesafe,
 * kademeler) ile sözlük (hangi sözcük ayırt edici değildir) ayrı hızlarda
 * büyür. Sözlük her yeni kurum türüyle uzar; algoritma uzamaz. Dosya tavanı
 * (`file-size-ceiling.guard.spec.ts`, kaynak 500) bu ayrımı zorunlu kıldı.
 *
 * Tablolar İNSAN OKUNUR yazılır; makine karşılaştırması katlanmış hâlde yapılır
 * (`fold()` ölçüt dosyasındadır ve bu listeleri kendisi katlar).
 */
import { CITY_CODE_ABROAD, CITY_NAME_TO_CODE } from './city-catalog';
/**
 * GÖRÜNMEZ KARAKTERLER — silinir. Kod noktalarıyla yazılır, çünkü kaynakta
 * gerçek karakter olarak durursa ne okunur ne de gözden geçirilebilir.
 *
 * Sıfır genişlikli birleştirici/ayırıcılar (U+200B..U+200D), yön işaretleri
 * (U+200E..U+200F, U+202A..U+202E, U+2066..U+2069), yumuşak tire (U+00AD),
 * birleştirici grafem birleştirici (U+034F), Arapça harf ayırıcı (U+061C),
 * Moğol sesli ayırıcı (U+180E), sözcük birleştirici ve görünmez işlemciler
 * (U+2060..U+2064), biçim işaretleri (U+206A..U+206F) ve BOM (U+FEFF).
 *
 * `Sabancı` ile arasına U+200B konmuş yazımı ekranda AYRIŞTIRAN yoktur; ikisi
 * aynı iskelete inmezse kapı hiç kurulmamış demektir.
 */
export const ORGANIZATION_NAME_INVISIBLE_CODEPOINTS = [
    0x00ad, 0x034f, 0x061c, 0x180e, 0x200b, 0x200c, 0x200d, 0x200e, 0x200f, 0x202a, 0x202b, 0x202c,
    0x202d, 0x202e, 0x2060, 0x2061, 0x2062, 0x2063, 0x2064, 0x2066, 0x2067, 0x2068, 0x2069, 0x206a,
    0x206b, 0x206c, 0x206d, 0x206e, 0x206f, 0xfeff,
];
/**
 * KARIŞTIRILABİLİR SINIF TABLOSU — her sınıf TEK temsilciye iner.
 *
 * Tablo sınıf bazlıdır, çift bazlı DEĞİLDİR ve bu fark önemlidir: `1` hem `i`
 * hem `l` gibi görünür. Çift bazlı eşleme yapılsaydı `Unilever` → `unilever`
 * ama `Uni1ever` → `uniiever` olur ve ikisi ayrışırdı. Sınıf bazlı eşlemede
 * `i`, `ı`, `İ`, `I`, `l`, `L`, `1`, `|` hepsi `i`ye iner; iki ad da
 * `uniiever` olur ve çakışma yakalanır.
 *
 * Sınıflar üç kaynağı birden kapatır:
 *  - TÜRKÇE KATLAMA (zorunlu): `İ/I/ı/i` · `Ş/ş` · `Ğ/ğ` · `Ç/ç` · `Ö/ö` · `Ü/ü`
 *  - KİRİL/YUNAN HOMOGLİFLERİ: `а е о р с у х к м н т в` ve büyük karşılıkları
 *  - RAKAM-HARF İKAMESİ: `0/O` · `1/l` · `3/E` · `4/A` · `5/S` · `@/a` · `$/s`
 *
 * Yeni bir ikame sınıfı çıkarsa buraya eklenir ve
 * `organization-name-similarity.spec.ts` tablosu genişler.
 */
export const ORGANIZATION_NAME_CONFUSABLE_CLASSES = {
    a: 'aAàáâäãåÀÁÂÄÃÅ4@аАαΑ',
    b: 'bBьЬβ',
    c: 'cCçÇćĉсСϲς',
    d: 'dD',
    e: 'eEèéêëÈÉÊË3еЕєЄ',
    f: 'fF',
    g: 'gGğĞĝ',
    h: 'hHнНη',
    i: 'iIıİìíîïÌÍÎÏ1|lLĺĽ',
    j: 'jJ',
    k: 'kKкКκΚ',
    m: 'mMмМμΜ',
    n: 'nNñÑ',
    o: 'oOöÖòóôõÒÓÔÕØø0оОθΟ',
    p: 'pPрРρΡ',
    q: 'qQ',
    r: 'rR',
    s: 'sSşŞśŝ5$Ѕ',
    t: 'tTтТτΤ',
    u: 'uUüÜùúûÙÚÛ',
    v: 'vVνѵ',
    w: 'wWшШω',
    x: 'xXхХχΧ',
    y: 'yYýÿуУγ',
    z: 'zZžŽ',
};
/**
 * HUKUKİ EKLER — ayırt edici değildir, soyulur.
 *
 * Değerler İNSAN OKUNUR yazılır ve makine tarafında katlanır: `A.Ş.` noktaları
 * düştükten sonra `as`, `Şti.` → `sti`, `Vakfı` → `vakfi`. `Holding` de
 * buradadır: kullanıcının örneğinde ayırt edici sözcük `Sabancı`, `Holding`
 * değildir.
 */
export const ORGANIZATION_NAME_LEGAL_SUFFIXES = [
    'as',
    'sti',
    'ltd',
    'limited',
    'anonim',
    'sirketi',
    'sirket',
    'holding',
    'holdings',
    'vakfi',
    'vakif',
    'dernegi',
    'dernek',
    'kooperatifi',
    'inc',
    'incorporated',
    'llc',
    'lic',
    'gmbh',
    'ag',
    'sarl',
    'bv',
    'nv',
    'plc',
    'oy',
    'ab',
    'corp',
    'corporation',
    'company',
    'co',
];
/**
 * SEKTÖR/FAALİYET SÖZCÜKLERİ — marka değildir.
 *
 * `Teknoloji Derneği` doğrulanırsa, hukuki ek soyulduktan sonra tek sözcüğü
 * `teknoloji` kalır ve içerme kuralı "X Teknoloji" adlı HER kurumu doğrulamaya
 * iterdi.
 */
export const ORGANIZATION_NAME_SECTOR_TOKENS = [
    'teknoloji',
    'teknolojileri',
    'teknik',
    'yazilim',
    'dijital',
    'medya',
    'ajans',
    'ajansi',
    'etkinlik',
    'etkinlikleri',
    'organizasyon',
    'akademi',
    'akademisi',
    'egitim',
    'danismanlik',
    'girisim',
    'girisimcilik',
    'kultur',
    'sanat',
    'spor',
    'saglik',
    'enerji',
    'insaat',
    'gida',
    'turizm',
    'lojistik',
    'ticaret',
    'sanayi',
    'grup',
    'group',
    'tech',
    'technology',
    'software',
    'digital',
    'media',
    'agency',
    'events',
    'academy',
    'education',
    'consulting',
    'ventures',
    'labs',
    'studio',
    'solutions',
    'systems',
    'global',
    'international',
];
/**
 * KURUM TÜRÜ SÖZCÜKLERİ — düzeltme turu, bulgu 1 (tech-lead ölçümü).
 *
 * NEDEN EKLENDİ: `contains` kademesi korunan adın AYIRT EDİCİ sözcüklerinin
 * hepsini adayda arar. Küme TEK ELEMANLIYSA kural "o sözcüğü içeren HER ad"
 * hâline gelir ve tür sözcükleri kategori çapında kilitler:
 *
 *   `Ege Üniversitesi`  → küme `["universitesi"]` → Boğaziçi/Koç/Sabancı Ü. BLOKE
 *   `İş Bankası`        → küme `["bankasi"]`      → Ziraat/Halk/Vakıflar B. BLOKE
 *
 * `İş Bankası` vakası kuralın nasıl ters döndüğünü gösteriyor: gerçek marka
 * (`iş`, iki harf) ayırt edici sayılmayacak kadar kısa, korunan şey JENERİK
 * sözcük oluyor. İlk doğrulanan üniversite, Türkiye'deki bütün üniversiteleri
 * yayından düşürüyordu. Çıkış yolu doğrulama ama doğrulama İNSAN ONAYI: duvar
 * gerçek.
 *
 * TAKAS: tek ayırt edici sözcüğü tür sözcüğü olan kurum `contains` korumasını
 * kaybeder. İSKELET ve MESAFE kademeleri çalışmaya devam eder — yani birebir
 * ad, homoglif yazım ve tek harf farkı hâlâ yakalanır. Taklit için asıl yüzey
 * budur; "aynı türden başka bir kurum" taklit değildir.
 */
export const ORGANIZATION_NAME_INSTITUTION_TYPE_TOKENS = [
    'universite',
    'universitesi',
    'fakulte',
    'fakultesi',
    'enstitu',
    'enstitusu',
    'okul',
    'okulu',
    'okullari',
    'lise',
    'lisesi',
    'kolej',
    'koleji',
    'banka',
    'bankasi',
    'bankacilik',
    'sigorta',
    'sigortasi',
    'grubu',
    'toplulugu',
    'belediye',
    'belediyesi',
    'hastane',
    'hastanesi',
    'federasyon',
    'federasyonu',
    'konfederasyonu',
    'kulup',
    'kulubu',
    'oda',
    'odasi',
    'birlik',
    'birligi',
    'sendika',
    'sendikasi',
    'kooperatif',
    'kooperatifi',
    'merkez',
    'merkezi',
];
/**
 * BÖLGE / YÖN / YAYGIN YER SÖZCÜKLERİ — aynı gerekçe, coğrafi ayak.
 *
 * `Anadolu Ajansı` doğrulanınca küme `["anadolu"]`e iniyor ve Anadolu Efes,
 * Anadolu Sigorta, Anadolu Üniversitesi bloke oluyordu; `Türk Eğitim Vakfı`
 * `["turk"]`e inip Türk Telekom'u ve THY'yi kapatıyordu. Coğrafya kimsenin
 * markası değildir.
 *
 * `yildiz` BİLİNÇLİ olarak burada: hem yer adıdır (Yıldız/Beşiktaş, Yıldız
 * Sarayı) hem de birden çok kurumun ortak sözcüğüdür — `Yıldız Holding`
 * doğrulanınca `Yıldız Teknik Üniversitesi` bloke oluyordu (tech-lead tablosu,
 * 5. satır). Listeye giren tek "il olmayan" özel ad budur ve raporlanmıştır.
 */
export const ORGANIZATION_NAME_PLACE_TOKENS = [
    'anadolu',
    'turkiye',
    'turk',
    'ege',
    'marmara',
    'akdeniz',
    'karadeniz',
    'trakya',
    'avrupa',
    'asya',
    'avrasya',
    'balkan',
    'dogu',
    'bati',
    'kuzey',
    'guney',
    'orta',
    'yildiz',
];
/**
 * İL ADLARI KATALOGDAN TÜRETİLİR — elle ikinci liste tutulmaz.
 *
 * `CITY_NAME_TO_CODE` zaten normalleştirilmiş il adlarını (ve yaygın alternatif
 * yazımları: Afyon, Antep, Maraş, Urfa, İçel, İzmit, Adapazarı, Antakya) taşır.
 * Yeni il/alternatif eklendiğinde bu liste KENDİLİĞİNDEN büyür; bayatlayamaz.
 * `ABROAD` girdileri (yurtdışı) bir yer adı değil, katalog kaçışıdır.
 */
export const ORGANIZATION_NAME_CITY_TOKENS = Object.entries(CITY_NAME_TO_CODE)
    .filter(([, code]) => code !== CITY_CODE_ABROAD)
    .map(([name]) => name);
/**
 * AYIRT EDİCİ OLMAYAN SÖZCÜKLER — `contains` kademesini TETİKLEMEZ.
 *
 * Üç kategori: sektör · kurum türü · coğrafya. Mesafe ve iskelet kademeleri bu
 * sözcükler için ÇALIŞMAYA DEVAM EDER.
 */
export const ORGANIZATION_NAME_GENERIC_TOKENS = [
    ...ORGANIZATION_NAME_SECTOR_TOKENS,
    ...ORGANIZATION_NAME_INSTITUTION_TYPE_TOKENS,
    ...ORGANIZATION_NAME_PLACE_TOKENS,
    ...ORGANIZATION_NAME_CITY_TOKENS,
];
//# sourceMappingURL=organization-name-vocabulary.js.map